import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tile } from "@react-gcc-eds/core";

import { defaultOrdersFilters, IWeekMetric, useStore } from "../../../../store";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { parse } from "date-fns";
import { maxMetricCreationDate, minMetricCreationDate } from "../utils";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { KpiContainer } from "../../helpers/dashboard-kpis/kpi-container";
import { useLocalization } from "../../../../utils/localization";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";

const MeetRequestedDate = ({
  labels,
  customerId,
  className,
  alignment,
  onItemSelected
}: {
  labels: { [key: string]: string | undefined };
  customerId: string;
  className: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const [state] = useStore();
  const tileRef = useRef<HTMLDivElement>(null);
  const { localeString } = useLocalization();
  const { orderMeetRequestedDateMetric, itemMeetRequestedDateMetric } = state.fetchingData.metrics;
  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);
  const getTabs = useCallback(() => {
    const tabs = [];
    if (labels["purchaseOrders"] !== undefined) {
      tabs.push(orderTab);
    }
    if (labels["purchaseOrderItems"] !== undefined) {
      tabs.push(itemsTab);
    }
    return tabs;
  }, [orderMeetRequestedDateMetric, itemMeetRequestedDateMetric]);

  const { getOrderMeetRequestedDateMetric, getItemMeetRequestedDateMetric } = useMetricsActions();

  useEffect(() => {
    getOrderMeetRequestedDateMetric();
    getItemMeetRequestedDateMetric();
  }, [customerId]);

  const handleItemSelected = (
    metric: IWeekMetric,
    allMetrics: IWeekMetric[],
    graph: DashboardGraph
  ) => {
    if (!onItemSelected) {
      return undefined;
    }

    return () => {
      onItemSelected({
        filters: {
          orderCreationRange: {
            from: minMetricCreationDate(allMetrics),
            to: maxMetricCreationDate(allMetrics)
          },
          requestedDeliveryRange: {
            from: parse(metric.startDate, "yyyy-MM-dd", new Date(1901, 1, 1)),
            to: parse(metric.endDate, "yyyy-MM-dd", new Date(1901, 1, 1))
          },
          meetRequested: defaultOrdersFilters.meetRequested.map(f =>
            f.accessor === "no" ? { ...f, selected: true } : { ...f, selected: false }
          )
        },
        graph
      });
    };
  };

  const tileHeader = (
    <DashboardTooltipHeader
      header={localeString("Dashboard_DoesNotMeetRequestedDate") || "Does not meet requested date"}
      tooltipInfo="If Confirmed date date is more than 2 days after Requested delivery date"
      alignment={alignment}
    />
  );

  const orderTab = {
    view: KpiContainer({
      metricLoading: orderMeetRequestedDateMetric === true,
      metrics: state.metrics.orderMeetRequestedDateMetric,
      onKPIClick: handleItemSelected,
      amountColorCondition: (amount: number) => amount > 0,
      kpiBottomText: "Orders"
    }),
    title: labels["purchaseOrders"],
    index: 0
  } as ITab;

  const itemsTab = {
    view: KpiContainer({
      metricLoading: itemMeetRequestedDateMetric === true,
      metrics: state.metrics.itemMeetRequestedDateMetric,
      onKPIClick: handleItemSelected,
      amountColorCondition: (amount: number) => amount > 0,
      kpiBottomText: "Items"
    }),
    title: labels["purchaseOrderItems"],
    index: 1
  } as ITab;

  const [_, setSelectedTab] = useState<ITab>(orderTab);

  return (
    <>
      <Tile
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
      >
        <CustomTabs onTabChange={(tab: ITab) => setSelectedTab(tab)} tabs={getTabs()} />
      </Tile>
    </>
  );
};

export default MeetRequestedDate;
